import React, { useState, useEffect } from 'react';
import { Wrench, ArrowSquareOut, Palette, Storefront, Envelope, CreditCard, Link, Video, Calendar, Buildings, MegaphoneSimple, MagnifyingGlass, Newspaper } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import api from '@/utils/api';

// Icon mapping
const ICON_MAP = {
  'palette': Palette,
  'storefront': Storefront,
  'envelope': Envelope,
  'credit-card': CreditCard,
  'link': Link,
  'video': Video,
  'calendar': Calendar,
  'building': Buildings,
  'megaphone': MegaphoneSimple,
  'search': MagnifyingGlass,
  'newspaper': Newspaper,
};

/**
 * ToolLinks Component
 * 
 * Displays recommended tools for building/executing PayGBot outputs.
 * Simple buttons, mobile-friendly, one-line instructions.
 * 
 * Props:
 * - outputType: 'lead_magnet' | 'sales_page' | 'content' | 'weekly_plan' | 'advertising' | 'email_campaign'
 * - className: Additional CSS classes
 * - compact: Show compact view (default: false)
 */
const ToolLinks = ({ outputType, className = '', compact = false }) => {
  const [toolData, setToolData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchToolLinks();
  }, [outputType]);

  const fetchToolLinks = async () => {
    try {
      const response = await api.get(`/tool-links/${outputType}`);
      if (response.data.success) {
        setToolData(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch tool links:', error);
      // Use fallback data
      setToolData(getFallbackData(outputType));
    } finally {
      setLoading(false);
    }
  };

  const getFallbackData = (type) => {
    const fallbacks = {
      lead_magnet: {
        title: 'Tools to Build This',
        description: 'Create your lead magnet:',
        tools: [
          { id: 'canva', name: 'Canva', description: 'Design your PDF', url: 'https://canva.com', icon: 'palette' },
          { id: 'stan_store', name: 'Stan Store', description: 'Host & deliver', url: 'https://stan.store', icon: 'storefront' },
          { id: 'mailchimp', name: 'Mailchimp', description: 'Collect emails', url: 'https://mailchimp.com', icon: 'envelope' },
        ]
      },
      sales_page: {
        title: 'Tools to Build This',
        description: 'Launch your sales page:',
        tools: [
          { id: 'stan_store', name: 'Stan Store', description: 'Host your page', url: 'https://stan.store', icon: 'storefront' },
          { id: 'stripe', name: 'Stripe', description: 'Accept payments', url: 'https://stripe.com', icon: 'credit-card' },
          { id: 'linktree', name: 'Linktree', description: 'Link-in-bio', url: 'https://linktr.ee', icon: 'link' },
        ]
      },
      content: {
        title: 'Tools to Build This',
        description: 'Create your content:',
        tools: [
          { id: 'canva', name: 'Canva', description: 'Design graphics', url: 'https://canva.com', icon: 'palette' },
          { id: 'capcut', name: 'CapCut', description: 'Edit videos', url: 'https://capcut.com', icon: 'video' },
          { id: 'later', name: 'Later', description: 'Schedule posts', url: 'https://later.com', icon: 'calendar' },
        ]
      },
      weekly_plan: {
        title: 'Tools to Execute Your Plan',
        description: 'Complete your tasks:',
        tools: [
          { id: 'canva', name: 'Canva', description: 'Create graphics', url: 'https://canva.com', icon: 'palette' },
          { id: 'mailchimp', name: 'Mailchimp', description: 'Send emails', url: 'https://mailchimp.com', icon: 'envelope' },
          { id: 'google_business', name: 'Google Business', description: 'Update profile', url: 'https://business.google.com', icon: 'building' },
        ]
      },
      advertising: {
        title: 'Tools to Promote This',
        description: 'Run ads:',
        tools: [
          { id: 'meta_ads', name: 'Meta Ads', description: 'FB & IG ads', url: 'https://business.facebook.com/ads', icon: 'megaphone' },
          { id: 'google_ads', name: 'Google Ads', description: 'Search ads', url: 'https://ads.google.com', icon: 'search' },
          { id: 'youtube_ads', name: 'YouTube Ads', description: 'Video ads', url: 'https://youtube.com/ads', icon: 'video' },
        ]
      },
      email_campaign: {
        title: 'Tools to Send Emails',
        description: 'Email your customers:',
        tools: [
          { id: 'mailchimp', name: 'Mailchimp', description: 'Email marketing', url: 'https://mailchimp.com', icon: 'envelope' },
          { id: 'convertkit', name: 'ConvertKit', description: 'Creator emails', url: 'https://convertkit.com', icon: 'envelope' },
          { id: 'beehiiv', name: 'Beehiiv', description: 'Newsletters', url: 'https://beehiiv.com', icon: 'newspaper' },
        ]
      },
    };
    return fallbacks[type] || fallbacks.content;
  };

  const handleToolClick = (tool) => {
    // Track click for analytics (optional)
    try {
      api.post('/analytics/tool-click', { tool_id: tool.id, output_type: outputType }).catch(() => {});
    } catch {}
    
    // Open link in new tab
    window.open(tool.url, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className={`animate-pulse bg-white/5 rounded-lg p-3 ${className}`}>
        <div className="h-4 bg-white/10 rounded w-32 mb-2"></div>
        <div className="flex gap-2">
          <div className="h-8 bg-white/10 rounded w-20"></div>
          <div className="h-8 bg-white/10 rounded w-20"></div>
          <div className="h-8 bg-white/10 rounded w-20"></div>
        </div>
      </div>
    );
  }

  if (!toolData || !toolData.tools?.length) {
    return null;
  }

  // Compact view - just buttons
  if (compact) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`${className}`}
        data-testid="tool-links-compact"
      >
        <div className="flex items-center gap-2 mb-2">
          <Wrench size={14} className="text-slate-400" />
          <span className="text-xs text-slate-400 font-medium">{toolData.title}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {toolData.tools.map((tool) => {
            const IconComponent = ICON_MAP[tool.icon] || Wrench;
            return (
              <button
                key={tool.id}
                onClick={() => handleToolClick(tool)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-lg text-xs text-slate-300 hover:text-white transition-all"
                data-testid={`tool-link-${tool.id}`}
              >
                <IconComponent size={14} weight="fill" />
                <span>{tool.name}</span>
                <ArrowSquareOut size={10} className="text-slate-500" />
              </button>
            );
          })}
        </div>
      </motion.div>
    );
  }

  // Full view with descriptions
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-xl border border-white/10 p-4 ${className}`}
      data-testid="tool-links-full"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
          <Wrench size={16} weight="fill" className="text-blue-400" />
        </div>
        <div>
          <h4 className="text-sm font-semibold text-white">{toolData.title}</h4>
          <p className="text-xs text-slate-400">{toolData.description}</p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {toolData.tools.map((tool) => {
          const IconComponent = ICON_MAP[tool.icon] || Wrench;
          return (
            <button
              key={tool.id}
              onClick={() => handleToolClick(tool)}
              className="flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-blue-500/30 rounded-lg transition-all group text-left"
              data-testid={`tool-link-${tool.id}`}
            >
              <div className="w-9 h-9 bg-white/10 group-hover:bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors">
                <IconComponent size={18} weight="fill" className="text-slate-400 group-hover:text-blue-400 transition-colors" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1">
                  <span className="text-sm font-medium text-white">{tool.name}</span>
                  <ArrowSquareOut size={12} className="text-slate-500 group-hover:text-blue-400 transition-colors" />
                </div>
                <p className="text-xs text-slate-500 truncate">{tool.description}</p>
              </div>
            </button>
          );
        })}
      </div>
    </motion.div>
  );
};

export default ToolLinks;
